import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Users, FileText, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";

const JOBS_API = "https://nspo-backend-gray.vercel.app/api/jobs";
const APPLICANTS_API = "https://nspo-backend-gray.vercel.app/api/applicants/by-job";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A569BD", "#E74C3C", "#3498DB", "#2ECC71"];

const StatsTab = () => {
  const [stats, setStats] = useState({
    jobs: [],
    applicantsByJob: [],
    statusCounts: { pending: 0, "under-review": 0, accepted: 0, rejected: 0 },
    totalApplicants: 0,
    activeJobs: 0,
    expiredJobs: 0
  });
  const [loading, setLoading] = useState(false);

  const getToken = () => localStorage.getItem("adminToken");

  // 🟢 Fetch all statistics
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = getToken();
        if (!token) {
          toast.error("❌ يجب تسجيل الدخول أولاً");
          return;
        }

        // Fetch jobs
        const jobsRes = await fetch(JOBS_API, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const jobsData = await jobsRes.json();
        
        if (!jobsRes.ok) throw new Error("فشل في تحميل الوظائف");

        // Fetch applicants for each job and calculate statistics
        const applicantsByJob = await Promise.all(
          jobsData.map(async (job) => {
            try {
              const resApplicants = await fetch(`${APPLICANTS_API}/${job._id}`, {
                headers: { "Authorization": `Bearer ${token}` }
              });
              const applicants = await resApplicants.json();
              
              const statusCounts = applicants.reduce((acc, applicant) => {
                const status = applicant.status || "pending";
                acc[status] = (acc[status] || 0) + 1;
                return acc;
              }, { pending: 0, "under-review": 0, accepted: 0, rejected: 0 });

              return {
                jobId: job._id,
                jobTitle: job.title,
                company: job.company,
                totalApplicants: applicants.length,
                statusCounts,
                isActive: new Date(job.endDate) > new Date()
              };
            } catch {
              return {
                jobId: job._id,
                jobTitle: job.title,
                company: job.company,
                totalApplicants: 0,
                statusCounts: { pending: 0, "under-review": 0, accepted: 0, rejected: 0 },
                isActive: new Date(job.endDate) > new Date()
              };
            }
          })
        );

        // Calculate overall statistics
        const totalApplicants = applicantsByJob.reduce((sum, job) => sum + job.totalApplicants, 0);
        const statusCounts = applicantsByJob.reduce((acc, job) => {
          Object.keys(job.statusCounts).forEach(status => {
            acc[status] = (acc[status] || 0) + job.statusCounts[status];
          });
          return acc;
        }, { pending: 0, "under-review": 0, accepted: 0, rejected: 0 });

        const activeJobs = applicantsByJob.filter(job => job.isActive).length;
        const expiredJobs = applicantsByJob.filter(job => !job.isActive).length;

        setStats({
          jobs: jobsData,
          applicantsByJob,
          statusCounts,
          totalApplicants,
          activeJobs,
          expiredJobs
        });

      } catch (error) {
        toast.error("❌ فشل في تحميل الإحصائيات");
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Prepare data for charts
  const pieChartData = stats.applicantsByJob
    .filter(job => job.totalApplicants > 0)
    .map(job => ({
      name: job.jobTitle.length > 15 ? job.jobTitle.substring(0, 15) + "..." : job.jobTitle,
      value: job.totalApplicants,
      fullName: job.jobTitle,
      company: job.company
    }));

  const barChartData = stats.applicantsByJob
    .filter(job => job.totalApplicants > 0)
    .map(job => ({
      name: job.jobTitle.length > 12 ? job.jobTitle.substring(0, 12) + "..." : job.jobTitle,
      applicants: job.totalApplicants,
      pending: job.statusCounts.pending,
      accepted: job.statusCounts.accepted,
      rejected: job.statusCounts.rejected
    }));

  const statusData = [
    { name: "قيد الانتظار", value: stats.statusCounts.pending, color: "#FFBB28", icon: Clock },
    { name: "قيد المراجعة", value: stats.statusCounts["under-review"], color: "#0088FE", icon: FileText },
    { name: "مقبول", value: stats.statusCounts.accepted, color: "#00C49F", icon: CheckCircle },
    { name: "مرفوض", value: stats.statusCounts.rejected, color: "#FF8042", icon: XCircle }
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">جارٍ تحميل الإحصائيات...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 📊 Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Applicants Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">إجمالي المتقدمين</p>
              <p className="text-3xl font-bold mt-2">{stats.totalApplicants}</p>
            </div>
            <Users className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        {/* Active Jobs Card */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">الوظائف النشطة</p>
              <p className="text-3xl font-bold mt-2">{stats.activeJobs}</p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-200" />
          </div>
        </div>

        {/* Pending Applications Card */}
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">طلبات قيد الانتظار</p>
              <p className="text-3xl font-bold mt-2">{stats.statusCounts.pending}</p>
            </div>
            <Clock className="w-12 h-12 text-yellow-200" />
          </div>
        </div>

        {/* Expired Jobs Card */}
        <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white p-6 rounded-2xl shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-100 text-sm">الوظائف المنتهية</p>
              <p className="text-3xl font-bold mt-2">{stats.expiredJobs}</p>
            </div>
            <XCircle className="w-12 h-12 text-gray-200" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 📈 Applicants per Job - Bar Chart */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-semibold mb-6 text-gray-800 flex items-center gap-2">
            <BarChart className="w-6 h-6 text-blue-600" />
            توزيع المتقدمين حسب الوظيفة
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="applicants" name="إجمالي المتقدمين" fill="#0088FE" />
              <Bar dataKey="pending" name="قيد الانتظار" fill="#FFBB28" />
              <Bar dataKey="accepted" name="مقبول" fill="#00C49F" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 🥧 Applications Status Pie Chart */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h3 className="text-xl font-semibold mb-6 text-gray-800 flex items-center gap-2">
            <PieChart className="w-6 h-6 text-green-600" />
            حالة الطلبات
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData.filter(item => item.value > 0)}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, value }) => `${name}: ${value}`}
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 📋 Detailed Applicants per Job */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-xl font-semibold mb-6 text-gray-800 flex items-center gap-2">
          <Users className="w-6 h-6 text-purple-600" />
          تفاصيل المتقدمين لكل وظيفة
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stats.applicantsByJob.map((job) => (
            <div
              key={job.jobId}
              className={`border rounded-xl p-4 transition-all duration-300 hover:shadow-lg ${
                job.isActive ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-semibold text-gray-800 text-sm leading-tight">
                  {job.jobTitle}
                </h4>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    job.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {job.isActive ? "نشطة" : "منتهية"}
                </span>
              </div>
              
              <p className="text-xs text-gray-600 mb-3">{job.company}</p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">إجمالي المتقدمين:</span>
                  <span className="font-bold text-blue-600">{job.totalApplicants}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">قيد الانتظار:</span>
                  <span className="font-bold text-yellow-600">{job.statusCounts.pending}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">مقبول:</span>
                  <span className="font-bold text-green-600">{job.statusCounts.accepted}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">مرفوض:</span>
                  <span className="font-bold text-red-600">{job.statusCounts.rejected}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatsTab;
